import { useParams } from '@reach/router';
import Tippy from '@tippyjs/react';
import { graphql } from 'gatsby';
import gql from 'graphql-tag';
import { startCase } from 'lodash/fp';
import React, { FC } from 'react';
import { MdInfo } from 'react-icons/md';
import { useQuery } from 'urql';

import { BooleanValue } from '@/bits';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
  Value,
} from '@/components';
import { EditIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  PlayerUpdateCancelWithdrawalBlockForm,
  PlayerUpdateCustomerWhiteListStatusForm,
  PlayerUpdateDepositsBlockForm,
  PlayerUpdateGamanzaBlockForm,
  PlayerUpdateIncreaseDepositLimitBlockForm,
  PlayerUpdateMarketingBlockForm,
  PlayerUpdateRewardsBlockForm,
  PlayerUpdateRgNetDepositLimitBlockForm,
} from '@/forms';
import { PlayerReopenCustomerForm } from '@/forms/player-reopen-customer-form';
import { CustomerRestrictionFields } from '@/fragments/CustomerRestriction';
import { CustomerRestrictionFieldsFragment } from '@/fragments/__generated__/CustomerRestriction';
import { getInitiator, isAgentInitiator } from '@/utils';
import formatDate from '@/utils/formatter/formatDate';
import {
  PlayerRgBlocksOverviewBoxQuery,
  PlayerRgBlocksOverviewBoxQueryVariables,
} from './__generated__/component';

export const Fragment = graphql`
  fragment SanityPlayerRgBlocksOverviewBlock on SanityPlayerRgBlocksOverviewBlock {
    title {
      ...SanityLocaleString
    }
    playerGlobalUUID {
      ...SanityLocaleString
    }
    rewardsBlocked {
      ...SanityLocaleString
    }
    gamanzaBlocked {
      ...SanityLocaleString
    }
    depositsBlocked {
      ...SanityLocaleString
    }
    rgNetDepositLimitBlocked {
      ...SanityLocaleString
    }
    increaseDepositLimitBlocked {
      ...SanityLocaleString
    }
    cancelWithdrawalBlocked {
      ...SanityLocaleString
    }
    customerStatus {
      ...SanityLocaleString
    }
    marketingBlocked {
      ...SanityLocaleString
    }
    closed {
      ...SanityLocaleString
    }
    isWhitelisted {
      ...SanityLocaleString
    }
  }
`;

const QUERY = gql`
  query PlayerRgBlocksOverviewBox($playerId: ID!) {
    player(playerId: $playerId) {
      id
      playerGlobalId
      customerV2 {
        id
        playerGlobalUUID
        cancelWithdrawalBlock {
          ...CustomerRestrictionFields
        }
        increaseDepositLimitBlock {
          ...CustomerRestrictionFields
        }
        rewardsBlock {
          ...CustomerRestrictionFields
        }
        depositsBlock {
          ...CustomerRestrictionFields
        }
        marketingBlock {
          ...CustomerRestrictionFields
        }
        rgNetDepositLimit {
          ...CustomerRestrictionFields
        }
        gamanzaBlock {
          ...CustomerRestrictionFields
        }
        isCustomerWhitelisted
        customerClosure {
          closeAccountCause
        }
      }
    }
  }
  ${CustomerRestrictionFields}
`;

const RestrictionInfo: FC<{
  restriction: CustomerRestrictionFieldsFragment;
}> = ({ restriction }) => {
  const { updatedBy, updatedAt } = restriction;

  if (!isAgentInitiator(updatedBy) || !updatedBy.agent) {
    return null;
  }

  const content = `${getInitiator(updatedBy)} - ${formatDate(updatedAt)}`;

  return (
    <Tippy content={content}>
      <button
        type="button"
        aria-label={content}
        className="mr-1 inline-flex items-center text-gray-600 dark:text-gray-200 focus:outline-none"
      >
        <MdInfo />
      </button>
    </Tippy>
  );
};

const PlayerRgBlocksOverviewBlock: FC<{
  block: Queries.SanityPlayerRgBlocksOverviewBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const { playerId } = useParams();
  const shouldBeDisplayed = process.env.GATSBY_LICENSE === 'MGA';

  const [{ data, fetching }, refresh] = useQuery<
    PlayerRgBlocksOverviewBoxQuery,
    PlayerRgBlocksOverviewBoxQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId,
    },
    // TODO: Temporary fix for until we have a better solution for this DEV-4241
    pause: !shouldBeDisplayed,
  });

  if (!shouldBeDisplayed) {
    return null;
  }

  if (!data || !data.player || !data.player.customerV2) {
    return null;
  }

  const customer = data.player.customerV2;
  const playerGlobalId = data.player.playerGlobalId;
  const customerStatus = customer.customerClosure?.closeAccountCause;

  return (
    <Card
      size="lg"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <Value
            fetching={fetching}
            title={t(block.playerGlobalUUID)}
            value={customer.playerGlobalUUID}
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.rewardsBlocked)}
            value={customer.rewardsBlock.isApplied}
            prefix={<RestrictionInfo restriction={customer.rewardsBlock} />}
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateRewardsBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.rewardsBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title="Gamanza Blocked"
            value={customer.gamanzaBlock.isApplied}
            prefix={<RestrictionInfo restriction={customer.gamanzaBlock} />}
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateGamanzaBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.gamanzaBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.cancelWithdrawalBlocked)}
            value={customer.cancelWithdrawalBlock.isApplied}
            prefix={
              <RestrictionInfo restriction={customer.cancelWithdrawalBlock} />
            }
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateCancelWithdrawalBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.cancelWithdrawalBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <Value
            fetching={fetching}
            title={t(block.customerStatus)}
            suffix={
              customerStatus &&
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerReopenCustomerForm playerGlobalId={playerGlobalId} />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          >
            {customerStatus && (
              <Tippy content={startCase(customerStatus)}>
                <span>{t(block.closed)}</span>
              </Tippy>
            )}
          </Value>
          <BooleanValue
            fetching={fetching}
            title={t(block.depositsBlocked)}
            value={customer.depositsBlock.isApplied}
            prefix={<RestrictionInfo restriction={customer.depositsBlock} />}
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateDepositsBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.depositsBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.increaseDepositLimitBlocked)}
            value={customer.increaseDepositLimitBlock.isApplied}
            prefix={
              <RestrictionInfo
                restriction={customer.increaseDepositLimitBlock}
              />
            }
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateIncreaseDepositLimitBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.increaseDepositLimitBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.marketingBlocked)}
            value={customer.marketingBlock.isApplied}
            prefix={<RestrictionInfo restriction={customer.marketingBlock} />}
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateMarketingBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.marketingBlock.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.rgNetDepositLimitBlocked)}
            value={customer.rgNetDepositLimit.isApplied}
            prefix={
              <RestrictionInfo restriction={customer.rgNetDepositLimit} />
            }
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateRgNetDepositLimitBlockForm
                      playerGlobalId={playerGlobalId}
                      isBlocked={customer.rgNetDepositLimit.isApplied}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
          <BooleanValue
            fetching={fetching}
            title={t(block.isWhitelisted)}
            value={customer.isCustomerWhitelisted}
            suffix={
              playerGlobalId && (
                <ControlledModal
                  content={
                    <PlayerUpdateCustomerWhiteListStatusForm
                      playerGlobalId={playerGlobalId}
                      isWhitelisted={customer.isCustomerWhitelisted}
                    />
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              )
            }
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerRgBlocksOverviewBlock;
